import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import logoFooter from './../../assets/logos/logoFooter.webp';

const Footer = () => {
  const { t } = useTranslation('nav');
  const lp = useLocalizedPath();

  const footerColumns = [
    {
      title: t('footer.comparators'),
      links: [
        { label: t('footer.creditCards'), routeKey: 'creditCards' },
        { label: t('footer.bankAccounts'), routeKey: 'accountComparator' },
        { label: t('footer.mortgages'), routeKey: 'mortgageComparator' },
        { label: t('footer.remittances'), routeKey: 'remittanceComparator' },
        { label: t('footer.insurance'), routeKey: 'insuranceComparator' },
        { label: t('footer.gicRates'), routeKey: 'gicRates' },
      ],
    },
    {
      title: t('footer.about'),
      links: [
        { label: t('footer.aboutFinMaple'), routeKey: 'about' },
        { label: t('footer.getStarted'), routeKey: 'getStarted' },
        { label: t('footer.contact'), routeKey: 'contact' },
      ],
    },
    {
      title: t('footer.methodology'),
      links: [
        { label: t('footer.howWeRate'), routeKey: 'howWeMonetize' },
        { label: t('footer.finmapleScore'), routeKey: 'howWeMonetize' },
        { label: t('footer.adDisclosure'), routeKey: 'howWeMonetize' },
      ],
    },
    {
      title: t('footer.legal'),
      links: [
        { label: t('footer.privacy'), routeKey: 'privacy' },
        { label: t('footer.terms'), routeKey: 'terms' },
        { label: t('footer.contact'), routeKey: 'contact' },
      ],
    },
  ];

  return (
    <Box
      component="footer"
      sx={{
        background: '#0B1220',
        borderTop: '1px solid rgba(255,255,255,0.10)',
        pt: { xs: 5, md: 6 },
        pb: { xs: 12, md: 5 },
        minHeight: { xs: 320, md: 340 },
      }}
    >
      <Container maxWidth="lg">
        {/* Desktop: 4-column grid — always rendered, CSS-hidden on mobile */}
        <Grid container spacing={4} sx={{ mb: 6, display: { xs: 'none', md: 'flex' } }}>
          {footerColumns.map((col) => (
            <Grid item xs={12} sm={6} md={3} key={col.title}>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#FFFFFF',
                  mb: 2.5,
                  lineHeight: '16px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {col.title}
              </Typography>
              <Box component="nav">
                {col.links.map((link, i) => (
                  <Typography key={`${link.label}-${i}`} sx={{ mb: 1.5 }}>
                    <Box
                      component={RouterLink}
                      to={lp(link.routeKey)}
                      sx={{
                        textDecoration: 'none',
                        color: '#B0B8C4',
                        fontSize: '13px',
                        lineHeight: '20px',
                        transition: 'color 0.2s ease',
                        '&:hover': { color: '#E6E9EE' },
                      }}
                    >
                      {link.label}
                    </Box>
                  </Typography>
                ))}
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Mobile: accordion — always rendered, CSS-hidden on desktop */}
        <Box sx={{ mb: 4, display: { xs: 'block', md: 'none' } }}>
          {footerColumns.map((col) => (
            <FooterAccordion key={col.title} title={col.title} links={col.links} lp={lp} t={t} />
          ))}
        </Box>

        {/* Logo + copyright */}
        <Box
          sx={{
            borderTop: '1px solid rgba(255,255,255,0.08)',
            pt: 4,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <img
              src={logoFooter}
              alt="FinMaple"
              loading="lazy"
              width={84}
              height={28}
              style={{
                height: '28px',
                width: 'auto',
                objectFit: 'contain',
                filter: 'invert(1) grayscale(1) contrast(9)',
                mixBlendMode: 'screen',
              }}
            />
            <Typography sx={{ fontSize: '12px', color: '#8896A6' }}>
              {`\u00A9 ${new Date().getFullYear()} FinMaple. ${t('footer.copyright')}`}
            </Typography>
          </Box>

          {/* Persistent trust links */}
          <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, justifyContent: { xs: 'center', sm: 'flex-end' } }}>
            {[
              { label: t('footer.methodology'), routeKey: 'howWeMonetize' },
              { label: t('footer.disclosure'), routeKey: 'howWeMonetize' },
              { label: t('footer.privacy'), routeKey: 'privacy' },
            ].map(({ label, routeKey }, i) => (
              <Box
                key={`${label}-${i}`}
                component={RouterLink}
                to={lp(routeKey)}
                sx={{
                  textDecoration: 'none',
                  color: '#8896A6',
                  fontSize: '12px',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: '#E6E9EE' },
                }}
              >
                {label}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Disclaimer */}
        <Typography
          sx={{
            fontSize: '12px',
            color: '#8896A6',
            lineHeight: '18px',
            mt: 4,
            maxWidth: '800px',
          }}
        >
          {t('footer.disclaimer')}
        </Typography>
      </Container>
    </Box>
  );
};

// Mobile accordion for footer columns
const FooterAccordion = ({ title, links, lp, t }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      <Box
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
          cursor: 'pointer',
        }}
      >
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#FFFFFF',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {title}
        </Typography>
        <IconButton
          size="small"
          sx={{ color: '#8896A6' }}
          aria-label={isOpen ? t('footer.collapseSection', { title }) : t('footer.expandSection', { title })}
        >
          <ExpandMoreIcon
            sx={{
              fontSize: '20px',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0)',
              transition: 'transform 0.2s ease',
            }}
          />
        </IconButton>
      </Box>
      {/* CSS display instead of MUI Collapse — avoids hydration mismatch
          (Collapse adds MuiCollapse-hidden class via effect) */}
      <Box sx={{ display: isOpen ? 'block' : 'none', pb: 2 }}>
        {links.map((link, i) => (
          <Typography key={`${link.label}-${i}`} sx={{ mb: 1.5 }}>
            <Box
              component={RouterLink}
              to={lp(link.routeKey)}
              sx={{
                textDecoration: 'none',
                color: '#B0B8C4',
                fontSize: '13px',
                transition: 'color 0.2s ease',
                '&:hover': { color: '#E6E9EE' },
              }}
            >
              {link.label}
            </Box>
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

/* ─── Compact footer for funnel/quiz entry pages ─────────────────── */
export const FooterCompact = () => {
  const { t } = useTranslation('nav');
  const lp = useLocalizedPath();

  const links = [
    { label: t('footer.howWeRate'), routeKey: 'howWeMonetize' },
    { label: t('footer.adDisclosure'), routeKey: 'howWeMonetize' },
    { label: t('footer.privacy'), routeKey: 'privacy' },
    { label: t('footer.contact'), routeKey: 'contact' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        background: '#0B1220',
        borderTop: '1px solid rgba(255,255,255,0.10)',
        py: { xs: 2, md: 2.5 },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <img
              src={logoFooter}
              alt="FinMaple"
              loading="lazy"
              width={84}
              height={28}
              style={{
                height: '24px',
                width: 'auto',
                objectFit: 'contain',
                filter: 'invert(1) grayscale(1) contrast(9)',
                mixBlendMode: 'screen',
              }}
            />
            <Typography sx={{ fontSize: '12px', color: '#8896A6' }}>
              {`\u00A9 ${new Date().getFullYear()} FinMaple`}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, flexWrap: 'wrap', justifyContent: 'center' }}>
            {links.map(({ label, routeKey }, i) => (
              <Box
                key={`${label}-${i}`}
                component={RouterLink}
                to={lp(routeKey)}
                sx={{
                  textDecoration: 'none',
                  color: '#8896A6',
                  fontSize: '12px',
                  transition: 'color 0.2s ease',
                  '&:hover': { color: '#E6E9EE' },
                }}
              >
                {label}
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;
